import { useState, useEffect, useRef } from "react";
import { useTheme } from "../components/ThemeProvider";
import api from "../services/api";
import { dialogs } from "../context/DialogContext";
import { inicialesNombreApellido } from "../utils/helpers";
import usePermiso from "../hooks/usePermiso";

/** Fallback si /permisos/catalogo falla — debe coincidir con permisosTree.ROLES */
const ROLES_FALLBACK = [
  { id: "admin", label: "Admin", color: "#2563eb" },
  { id: "director", label: "Director", color: "#7c3aed" },
  { id: "coordinador", label: "Coordinador", color: "#0891b2" },
  { id: "agente", label: "Agente", color: "#059669" },
  { id: "secretaria", label: "Secretaría", color: "#d97706" },
];
/** Valor visual cuando la contraseña SMTP ya está en BD (no se puede leer de vuelta). */
const SMTP_PASS_MASK = "••••••••••";

function smtpPassParaGuardar(raw) {
  const v = String(raw || "").trim();
  if (!v || v === SMTP_PASS_MASK) return undefined;
  return v;
}

// ── Subcomponente: zona de drop para imagen ──────────────────────────────────
/**
 * objectFitMode: "cover"   → recorta para llenar (bueno para retratos cuadrados)
 *                "contain" → encaja todo sin recortar, con fondo neutro (bueno para tarjetas apaisadas)
 * aspectRatio:  CSS aspect-ratio del contenedor, p.ej. "1/1" o "85.6/54" (estándar tarjeta)
 */
function ImageUploadZone({
  label, hint, currentUrl, fieldName, onUploaded, isDark, colors,
  objectFitMode = "cover", aspectRatio = "auto",
}) {
  const inputRef = useRef(null);
  const [preview, setPreview] = useState(currentUrl || null);
  const [dragging, setDragging] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => { setPreview(currentUrl || null); }, [currentUrl]);

  const handleFile = async (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) { setError("Solo se aceptan imágenes (JPG, PNG, WebP...)"); return; }
    setError("");
    const objectUrl = URL.createObjectURL(file);
    setPreview(objectUrl);
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append(fieldName, file);
      const res = await onUploaded(fd);
      const finalUrl = fieldName === "foto" ? res.data.avatar_url : res.data.tarjeta_url;
      setPreview(finalUrl || objectUrl);
    } catch (e) {
      setError(e.response?.data?.error || "Error al subir la imagen");
      setPreview(currentUrl || null);
    } finally {
      setUploading(false);
    }
  };

  const onInputChange = (e) => handleFile(e.target.files[0]);
  const onDrop = (e) => { e.preventDefault(); setDragging(false); handleFile(e.dataTransfer.files[0]); };
  const onDragOver = (e) => { e.preventDefault(); setDragging(true); };
  const onDragLeave = () => setDragging(false);

  const c = colors;
  const bgContain = isDark ? "rgba(255,255,255,0.04)" : "#f1f5f9";

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
      <label style={{ fontSize: 13, fontWeight: 600, color: c.textSecondary }}>{label}</label>
      <div
        onClick={() => !uploading && inputRef.current?.click()}
        onDrop={onDrop} onDragOver={onDragOver} onDragLeave={onDragLeave}
        style={{
          position: "relative",
          width: "100%",
          aspectRatio,
          /* fallback height solo si no hay aspect-ratio (navegadores muy viejos) */
          minHeight: 120,
          borderRadius: 12,
          border: `2px dashed ${dragging ? c.accentBlue : (isDark ? "rgba(255,255,255,0.15)" : "#cbd5e1")}`,
          background: dragging
            ? (isDark ? "rgba(59,130,246,0.1)" : "#eff6ff")
            : (preview && objectFitMode === "contain" ? bgContain : (isDark ? "rgba(255,255,255,0.04)" : "#f8fafc")),
          cursor: uploading ? "wait" : "pointer",
          display: "flex", alignItems: "center", justifyContent: "center",
          overflow: "hidden",
          transition: "all 0.15s",
        }}
      >
        {preview ? (
          <>
            <img
              src={preview}
              alt={label}
              style={{
                width: "100%",
                height: "100%",
                objectFit: objectFitMode,
                /* contain: pequeño padding para que no toque el borde */
                padding: objectFitMode === "contain" ? 8 : 0,
                boxSizing: "border-box",
              }}
            />
            <div style={{
              position: "absolute", inset: 0, background: "rgba(0,0,0,0.4)",
              display: "flex", alignItems: "center", justifyContent: "center",
              opacity: 0, transition: "opacity 0.15s",
            }}
              onMouseEnter={e => e.currentTarget.style.opacity = "1"}
              onMouseLeave={e => e.currentTarget.style.opacity = "0"}
            >
              <span style={{ color: "#fff", fontSize: 12, fontWeight: 600, background: "rgba(0,0,0,0.5)", padding: "4px 10px", borderRadius: 20 }}>
                {uploading ? "Subiendo…" : "Cambiar imagen"}
              </span>
            </div>
          </>
        ) : (
          <div style={{ textAlign: "center", padding: 16, pointerEvents: "none" }}>
            <div style={{ fontSize: 28, marginBottom: 6 }}>🖼️</div>
            <p style={{ fontSize: 12, fontWeight: 600, color: c.textSecondary, margin: 0 }}>
              {uploading ? "Subiendo…" : "Arrastra o haz clic"}
            </p>
            <p style={{ fontSize: 11, color: c.textMuted, margin: "2px 0 0" }}>{hint}</p>
          </div>
        )}
        {uploading && (
          <div style={{
            position: "absolute", inset: 0, background: "rgba(0,0,0,0.45)",
            display: "flex", alignItems: "center", justifyContent: "center",
          }}>
            <span style={{ color: "#fff", fontSize: 12, fontWeight: 600 }}>Subiendo…</span>
          </div>
        )}
      </div>
      <input ref={inputRef} type="file" accept="image/*" style={{ display: "none" }} onChange={onInputChange} />
      {error && <p style={{ fontSize: 11, color: "#ef4444", margin: 0 }}>{error}</p>}
      {preview && !uploading && (
        <a href={preview} target="_blank" rel="noopener noreferrer"
          style={{ fontSize: 11, color: c.accentBlue, textDecoration: "none" }}>Ver a tamaño completo ↗</a>
      )}
    </div>
  );
}

// ── Reposicionador de avatar ─────────────────────────────────────────────────
function parsePos(str) {
  const m = (str || "50% 50%").match(/(\d+(?:\.\d+)?)%\s+(\d+(?:\.\d+)?)%/);
  return m ? { x: parseFloat(m[1]), y: parseFloat(m[2]) } : { x: 50, y: 50 };
}

function AvatarReposicionador({ src, position, onSave, isDark, colors: c }) {
  const containerRef = useRef(null);
  const posRef = useRef(parsePos(position));
  const [posDisplay, setPosDisplay] = useState(parsePos(position));
  const [dragging, setDragging] = useState(false);
  const dragStartRef = useRef(null);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    const p = parsePos(position);
    posRef.current = p;
    setPosDisplay(p);
  }, [position]);

  const onMouseDown = (e) => {
    e.preventDefault();
    setDragging(true);
    setSaved(false);
    dragStartRef.current = {
      mouseX: e.clientX, mouseY: e.clientY,
      startX: posRef.current.x, startY: posRef.current.y,
    };
  };

  // Touch support
  const onTouchStart = (e) => {
    const t = e.touches[0];
    setDragging(true);
    setSaved(false);
    dragStartRef.current = {
      mouseX: t.clientX, mouseY: t.clientY,
      startX: posRef.current.x, startY: posRef.current.y,
    };
  };

  useEffect(() => {
    if (!dragging) return;
    const cw = containerRef.current?.offsetWidth || 180;
    const ch = containerRef.current?.offsetHeight || 180;
    // Sensitivity: ~1.5× so that dragging half the circle covers most range
    const sens = 160;

    const move = (cx, cy) => {
      const dx = cx - dragStartRef.current.mouseX;
      const dy = cy - dragStartRef.current.mouseY;
      const newX = Math.max(0, Math.min(100, dragStartRef.current.startX - dx / cw * sens));
      const newY = Math.max(0, Math.min(100, dragStartRef.current.startY - dy / ch * sens));
      posRef.current = { x: newX, y: newY };
      setPosDisplay({ x: newX, y: newY });
    };

    const onMove  = (e) => move(e.clientX, e.clientY);
    const onUp    = () => setDragging(false);
    const onTMove = (e) => { e.preventDefault(); move(e.touches[0].clientX, e.touches[0].clientY); };
    const onTEnd  = () => setDragging(false);

    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    window.addEventListener("touchmove", onTMove, { passive: false });
    window.addEventListener("touchend", onTEnd);
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
      window.removeEventListener("touchmove", onTMove);
      window.removeEventListener("touchend", onTEnd);
    };
  }, [dragging]);

  const posStr = `${Math.round(posDisplay.x)}% ${Math.round(posDisplay.y)}%`;

  const handleSave = () => {
    onSave(posStr);
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 10 }}>
      {/* Círculo interactivo */}
      <div
        ref={containerRef}
        onMouseDown={onMouseDown}
        onTouchStart={onTouchStart}
        title="Arrastra para reposicionar"
        style={{
          width: 160, height: 160,
          borderRadius: "50%",
          overflow: "hidden",
          cursor: dragging ? "grabbing" : "grab",
          userSelect: "none",
          flexShrink: 0,
          border: `3px solid ${c.accentBlue}`,
          boxShadow: `0 0 0 6px ${c.accentBlue}22, 0 4px 20px rgba(0,0,0,0.3)`,
          position: "relative",
        }}
      >
        <img
          src={src}
          alt="avatar"
          draggable={false}
          style={{
            width: "100%", height: "100%",
            objectFit: "cover",
            objectPosition: posStr,
            pointerEvents: "none",
            userSelect: "none",
            transition: dragging ? "none" : "object-position 0.1s",
          }}
        />
        {/* Guía de cruce */}
        <div style={{
          position: "absolute", inset: 0, pointerEvents: "none",
          display: "flex", alignItems: "center", justifyContent: "center",
        }}>
          <div style={{ position: "absolute", left: "50%", top: 0, bottom: 0, width: 1, background: "rgba(255,255,255,0.25)" }} />
          <div style={{ position: "absolute", top: "50%", left: 0, right: 0, height: 1, background: "rgba(255,255,255,0.25)" }} />
          <div style={{ width: 10, height: 10, borderRadius: "50%", background: "rgba(255,255,255,0.6)", border: "1.5px solid rgba(0,0,0,0.4)" }} />
        </div>
      </div>

      <p style={{ fontSize: 11, color: c.textMuted, margin: 0, textAlign: "center", lineHeight: 1.4 }}>
        ↔↕ Arrastra para reposicionar el encuadre
        <br />
        <span style={{ color: isDark ? "#94a3b8" : "#64748b" }}>{posStr}</span>
      </p>

      <button
        type="button"
        onClick={handleSave}
        style={{
          padding: "5px 16px", borderRadius: 7, border: "none",
          background: saved ? "#16a34a" : c.accentBlue,
          color: "#fff", fontSize: 12, fontWeight: 600, cursor: "pointer",
          transition: "background 0.2s",
        }}
      >
        {saved ? "✓ Guardado" : "Guardar posición"}
      </button>
    </div>
  );
}

function PasswordInputWithToggle({ name, value, onChange, placeholder, inputStyle, c, visible, onToggleVisible, autoComplete = "new-password", inputRef, onFocus, onBlur, disabled, title }) {
  return (
    <div style={{ position: "relative" }}>
      <input
        ref={inputRef}
        name={name}
        type={visible ? "text" : "password"}
        value={value}
        onChange={onChange}
        onInput={onChange}
        onFocus={onFocus}
        onBlur={onBlur}
        style={{ ...inputStyle, paddingRight: 40, boxSizing: "border-box" }}
        placeholder={placeholder}
        autoComplete={autoComplete}
        disabled={disabled}
        title={title}
      />
      <button
        type="button"
        onClick={onToggleVisible}
        title={visible ? "Ocultar contraseña" : "Mostrar contraseña"}
        aria-label={visible ? "Ocultar contraseña" : "Mostrar contraseña"}
        style={{
          position: "absolute",
          right: 6,
          top: "50%",
          transform: "translateY(-50%)",
          width: 28,
          height: 28,
          border: "none",
          borderRadius: 6,
          background: "transparent",
          color: c.textMuted,
          cursor: "pointer",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: 0,
        }}
      >
        {visible ? (
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M17.94 17.94A10.07 10.07 0 0112 20c-7 0-11-8-11-8a18.45 18.45 0 015.06-5.94" />
            <path d="M9.9 4.24A9.12 9.12 0 0112 4c7 0 11 8 11 8a18.5 18.5 0 01-2.16 3.19" />
            <line x1="1" y1="1" x2="23" y2="23" />
          </svg>
        ) : (
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
            <circle cx="12" cy="12" r="3" />
          </svg>
        )}
      </button>
    </div>
  );
}

// ── Página principal ──────────────────────────────────────────────────────────
export default function Usuarios() {
  const { theme } = useTheme();
  const c = theme.colors;
  const isDark = theme.name === "dark";
  const { puede } = usePermiso();
  const puedeCrearUsuario = puede("cfg_usuarios", "alta") && puede("cfg_usr_nuevo", "ver");
  const puedeEliminarUsuario = puede("cfg_usuarios", "baja") && puede("cfg_usr_eliminar", "ver");
  const veDatos = puede("cfg_usr_datos", "ver");
  const modDatos = puede("cfg_usr_datos", "modificar");
  const vePassword = puede("cfg_usr_password", "ver");
  const modPassword = puede("cfg_usr_password", "modificar");
  const veSmtp = puede("cfg_usr_smtp", "ver");
  const modSmtp = puede("cfg_usr_smtp", "modificar");
  const veFoto = puede("cfg_usr_foto", "ver");
  const modFoto = puede("cfg_usr_foto", "modificar");
  const veTarjeta = puede("cfg_usr_tarjeta", "ver");
  const modTarjeta = puede("cfg_usr_tarjeta", "modificar");
  const veActivar = puede("cfg_usr_activar", "ver");
  const modActivar = puede("cfg_usr_activar", "modificar");
  const veFiltros = puede("cfg_usr_filtros", "ver");

  const [usuarios, setUsuarios] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [mostrarForm, setMostrarForm] = useState(false);
  const [editando, setEditando] = useState(null);
  const [agentes, setAgentes] = useState([]);
  /** Roles del catálogo de Permisos (misma fuente que /configuracion/permisos). */
  const [rolesCatalogo, setRolesCatalogo] = useState(ROLES_FALLBACK);
  const [filtroRol, setFiltroRol] = useState("");
  const [filtroActivo, setFiltroActivo] = useState("");
  const [error, setError] = useState("");
  const [okMsg, setOkMsg] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showSmtpPass, setShowSmtpPass] = useState(false);
  const [probandoSmtp, setProbandoSmtp] = useState(false);
  const [smtpTestMsg, setSmtpTestMsg] = useState("");
  const [smtpTestOk, setSmtpTestOk] = useState(null);
  const passwordInputRef = useRef(null);
  const smtpPassInputRef = useRef(null);

  const formVacio = {
    nombre: "", email: "", password: "", rol: "agente",
    telefono: "", telefono_telnyx: "", agente_id: "", activo: true,
    avatar_url: "", avatar_position: "50% 50%", tarjeta_url: "",
    smtp_host: "mail.vitalcasa.com", smtp_port: 587, smtp_user: "", smtp_pass: "", smtp_configured: false,
  };
  const [form, setForm] = useState(formVacio);

  useEffect(() => {
    cargar();
    cargarAgentes();
    cargarRolesCatalogo();
  }, []);

  const cargarRolesCatalogo = async () => {
    try {
      const res = await api.get("/permisos/catalogo");
      const roles = res.data?.roles;
      if (Array.isArray(roles) && roles.length) {
        setRolesCatalogo(roles.map((r) => ({
          id: r.id,
          label: r.label || r.id,
          color: r.color || "#64748b",
        })));
      }
    } catch (err) {
      console.warn("Catálogo de roles no disponible; usando fallback:", err?.message || err);
    }
  };

  const cargar = async () => {
    try {
      const params = {};
      if (filtroRol) params.rol = filtroRol;
      if (filtroActivo !== "") params.activo = filtroActivo;
      const res = await api.get("/usuarios", { params });
      setUsuarios(res.data);
    } catch (err) {
      console.error("Error cargando usuarios:", err);
    } finally {
      setCargando(false);
    }
  };

  const cargarAgentes = async () => {
    try {
      const res = await api.get("/agentes");
      setAgentes(res.data.data || []);
    } catch (err) {
      console.error("Error cargando agentes:", err);
    }
  };

  useEffect(() => { cargar(); }, [filtroRol, filtroActivo]);

  const abrirNuevo = () => {
    setEditando(null);
    setForm(formVacio);
    setError("");
    setOkMsg("");
    setShowPassword(false);
    setShowSmtpPass(false);
    setSmtpTestMsg("");
    setSmtpTestOk(null);
    setMostrarForm(true);
  };

  const abrirEditar = (u) => {
    setEditando(u.id);
    setForm({
      nombre: u.nombre || "",
      email: u.email || "",
      password: "",
      rol: u.rol || "agente",
      telefono: u.telefono || "",
      telefono_telnyx: u.telefono_telnyx || "",
      agente_id: u.agente_id || "",
      activo: u.activo,
      avatar_url: u.avatar_url || "",
      avatar_position: u.avatar_position || "50% 50%",
      tarjeta_url: u.tarjeta_url || "",
      smtp_host: u.smtp_host || "mail.vitalcasa.com",
      smtp_port: u.smtp_port || 587,
      smtp_user: u.smtp_user || u.email || "",
      smtp_pass: u.smtp_configured ? SMTP_PASS_MASK : "",
      smtp_configured: u.smtp_configured === true,
    });
    setError("");
    setOkMsg("");
    setShowPassword(false);
    setShowSmtpPass(false);
    setSmtpTestMsg("");
    setSmtpTestOk(null);
    setMostrarForm(true);
  };

  const leerPasswordAcceso = () => {
    const fromState = String(form.password || "").trim();
    if (fromState) return fromState;
    return String(passwordInputRef.current?.value || "").trim();
  };

  const leerSmtpPass = () => {
    const fromState = String(form.smtp_pass || "").trim();
    if (fromState) return fromState;
    return String(smtpPassInputRef.current?.value || "").trim();
  };

  const probarCorreoSmtp = async () => {
    if (!modSmtp) return;
    setError("");
    setSmtpTestMsg("");
    setSmtpTestOk(null);
    setProbandoSmtp(true);
    try {
      const passRaw = leerSmtpPass();
      const res = await api.post("/usuarios/smtp/probar", {
        usuario_id: editando || undefined,
        smtp_host: form.smtp_host,
        smtp_port: form.smtp_port,
        smtp_user: form.smtp_user || form.email,
        smtp_pass: passRaw === SMTP_PASS_MASK ? "" : passRaw,
      });
      const d = res.data || {};
      setSmtpTestOk(true);
      setSmtpTestMsg(d.mensaje || "Correo correcto: enviar y recibir OK.");
    } catch (err) {
      const d = err.response?.data || {};
      setSmtpTestOk(false);
      setSmtpTestMsg(d.mensaje || d.error || "No se pudo comprobar el correo");
    } finally {
      setProbandoSmtp(false);
    }
  };

  const guardar = async () => {
    setError("");
    setOkMsg("");
    if (!form.nombre || !form.email) return setError("Nombre y email son obligatorios");

    const passwordAcceso = leerPasswordAcceso();

    if (!editando) {
      if (!passwordAcceso) return setError("La contraseña de acceso es obligatoria para nuevos usuarios");
      if (passwordAcceso.length < 6) return setError("La contraseña de acceso debe tener al menos 6 caracteres");
    } else if (passwordAcceso && passwordAcceso.length < 6) {
      return setError("La nueva contraseña de acceso debe tener al menos 6 caracteres");
    }

    try {
      if (editando) {
        const {
          password: _p,
          smtp_configured: _sc,
          ...sinPassword
        } = { ...form, agente_id: form.agente_id || null };

        const smtpPass = smtpPassParaGuardar(leerSmtpPass() || sinPassword.smtp_pass);
        if (smtpPass !== undefined) sinPassword.smtp_pass = smtpPass;
        else delete sinPassword.smtp_pass;

        if (passwordAcceso) {
          await api.patch("/usuarios/" + editando + "/password", { password: passwordAcceso });
        }
        await api.put("/usuarios/" + editando, sinPassword);
        if (passwordAcceso) {
          setOkMsg("Contraseña de acceso al CRM actualizada. Usa la nueva al iniciar sesión.");
        } else {
          setOkMsg("");
        }
      } else {
        const datos = { ...form, password: passwordAcceso, agente_id: form.agente_id || null };
        const smtpPass = smtpPassParaGuardar(leerSmtpPass() || datos.smtp_pass);
        if (smtpPass !== undefined) datos.smtp_pass = smtpPass;
        else delete datos.smtp_pass;
        await api.post("/usuarios", datos);
      }
      setMostrarForm(false);
      setEditando(null);
      cargar();
    } catch (err) {
      setError(err.response?.data?.error || "Error al guardar");
    }
  };

  const toggleActivo = async (u) => {
    try {
      await api.put("/usuarios/" + u.id, { activo: !u.activo });
      cargar();
    } catch (err) {
      await dialogs.alert("Error al cambiar estado");
    }
  };

  const eliminar = async (u) => {
    if (!(await dialogs.confirm(`Eliminar a ${u.nombre}? Esta accion no se puede deshacer.`))) return;
    try {
      await api.delete("/usuarios/" + u.id);
      cargar();
    } catch (err) {
      await dialogs.alert("Error al eliminar");
    }
  };

  const cambiar = (e) => {
    const { name, value } = e.target;
    setForm((prev) => {
      const next = { ...prev, [name]: name === "smtp_port" ? parseInt(value, 10) || 587 : value };
      if (name === "email" && !prev.smtp_user) next.smtp_user = value;
      return next;
    });
  };

  // Handlers de subida de imagen para un usuario YA EXISTENTE
  const handleUploadFoto = (id) => async (fd) => {
    const res = await api.post(`/usuarios/${id}/foto`, fd, { headers: { "Content-Type": "multipart/form-data" } });
    setForm(prev => ({ ...prev, avatar_url: res.data.avatar_url || prev.avatar_url, avatar_position: "50% 50%" }));
    cargar();
    return res;
  };

  const handleUploadTarjeta = (id) => async (fd) => {
    const res = await api.post(`/usuarios/${id}/tarjeta`, fd, { headers: { "Content-Type": "multipart/form-data" } });
    setForm(prev => ({ ...prev, tarjeta_url: res.data.tarjeta_url || prev.tarjeta_url }));
    cargar();
    return res;
  };

  const handleSavePosition = async (id, posStr) => {
    setForm(prev => ({ ...prev, avatar_position: posStr }));
    try {
      await api.put(`/usuarios/${id}`, { avatar_position: posStr });
      cargar();
    } catch (e) {
      console.error("Error guardando posición avatar:", e);
    }
  };

  const rolColor = (rol) => {
    const fromCat = rolesCatalogo.find((r) => r.id === rol);
    if (fromCat?.color) {
      return { bg: `${fromCat.color}22`, text: fromCat.color, border: `${fromCat.color}66` };
    }
    const colores = {
      admin:       { bg: "#fef3c7", text: "#92400e", border: "#fcd34d" },
      director:    { bg: "#f3e8ff", text: "#6b21a8", border: "#d8b4fe" },
      agente:      { bg: "#dbeafe", text: "#1e40af", border: "#93c5fd" },
      secretaria:  { bg: "#ede9fe", text: "#5b21b6", border: "#c4b5fd" },
      coordinador: { bg: "#d1fae5", text: "#065f46", border: "#6ee7b7" },
    };
    return colores[rol] || colores.agente;
  };

  const rolLabel = (rol) => rolesCatalogo.find((r) => r.id === rol)?.label
    || (rol ? rol.charAt(0).toUpperCase() + rol.slice(1) : rol);

  const inputStyle = {
    padding: "8px 12px", borderRadius: 8,
    border: `1px solid ${c.border}`,
    background: c.bgCard, color: c.textPrimary,
    fontSize: 14, outline: "none", width: "100%",
  };
  const labelStyle = { fontSize: 13, fontWeight: 600, color: c.textSecondary, marginBottom: 4 };

  return (
    <div style={{ padding: 24, maxWidth: 1200, margin: "0 auto" }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 24 }}>
        <div>
          <h1 style={{ fontSize: 22, fontWeight: 700, color: c.textPrimary, margin: 0 }}>Usuarios</h1>
          <p style={{ fontSize: 13, color: c.textMuted, margin: "4px 0 0" }}>{usuarios.length} usuarios registrados</p>
        </div>
        {puedeCrearUsuario && (
          <button onClick={abrirNuevo} style={{
            padding: "8px 20px", borderRadius: 8, border: "none", cursor: "pointer",
            background: c.accentBlue, color: "#fff", fontWeight: 600, fontSize: 14,
          }}>+ Nuevo usuario</button>
        )}
      </div>

      {okMsg && (
        <div style={{ padding: "10px 16px", borderRadius: 8, background: "rgba(22,163,74,0.08)", border: "1px solid rgba(22,163,74,0.25)", color: "#16a34a", fontSize: 13, marginBottom: 16 }}>
          ✓ {okMsg}
        </div>
      )}

      {/* Filtros */}
      {veFiltros && (
      <div style={{ display: "flex", gap: 12, marginBottom: 20, flexWrap: "wrap" }}>
        <select value={filtroRol} onChange={e => setFiltroRol(e.target.value)} style={{ ...inputStyle, width: "auto", minWidth: 160 }}>
          <option value="">Todos los roles</option>
          {rolesCatalogo.map(r => <option key={r.id} value={r.id}>{r.label}</option>)}
        </select>
        <select value={filtroActivo} onChange={e => setFiltroActivo(e.target.value)} style={{ ...inputStyle, width: "auto", minWidth: 140 }}>
          <option value="">Todos</option>
          <option value="true">Activos</option>
          <option value="false">Inactivos</option>
        </select>
      </div>
      )}

      {/* Formulario */}
      {mostrarForm && (
        <div style={{
          background: c.bgCard, borderRadius: 12, padding: 24, marginBottom: 24,
          border: `1px solid ${c.border}`,
        }}>
          <h3 style={{ fontSize: 14, fontWeight: 700, color: c.textSecondary, textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 20 }}>
            {editando ? "Editar" : "Nuevo"} usuario
          </h3>

          {error && (
            <div style={{ padding: "10px 16px", borderRadius: 8, background: "#fef2f2", border: "1px solid #fecaca", color: "#991b1b", fontSize: 13, marginBottom: 16 }}>
              {error}
            </div>
          )}

          {/* Campos de texto */}
          {veDatos && (
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(240px, 1fr))", gap: 16, marginBottom: 20 }}>
            <div style={{ display: "flex", flexDirection: "column" }}>
              <label style={labelStyle}>Nombre *</label>
              <input name="nombre" value={form.nombre} onChange={cambiar} style={inputStyle} placeholder="Nombre completo" disabled={!modDatos} title={!modDatos ? "No tienes permiso para modificar los datos del usuario" : undefined} />
            </div>
            <div style={{ display: "flex", flexDirection: "column" }}>
              <label style={labelStyle}>Email *</label>
              <input name="email" type="email" value={form.email} onChange={cambiar} style={inputStyle} placeholder="email@vitalcasa.com" disabled={!modDatos} title={!modDatos ? "No tienes permiso para modificar los datos del usuario" : undefined} />
            </div>
            {vePassword && (
            <div style={{ display: "flex", flexDirection: "column" }}>
              <label style={labelStyle}>Contraseña de acceso al CRM{editando ? "" : " *"}</label>
              <PasswordInputWithToggle
                inputRef={passwordInputRef}
                name="password"
                value={form.password}
                onChange={cambiar}
                placeholder={editando ? "Dejar vacío para no cambiar" : "Mín. 6 caracteres"}
                inputStyle={inputStyle}
                c={c}
                visible={showPassword}
                onToggleVisible={() => setShowPassword((v) => !v)}
                disabled={!modPassword}
                title={!modPassword ? "No tienes permiso para modificar la contraseña de acceso" : undefined}
              />
              {editando && (
                <p style={{ fontSize: 11, color: "#f59e0b", margin: "6px 0 0", lineHeight: 1.4 }}>
                  Solo para entrar en el CRM. La del buzón de correo va más abajo en «Contraseña del correo».
                </p>
              )}
            </div>
            )}
            <div style={{ display: "flex", flexDirection: "column" }}>
              <label style={labelStyle}>Rol</label>
              <select name="rol" value={form.rol} onChange={cambiar} style={inputStyle} disabled={!modDatos} title={!modDatos ? "No tienes permiso para modificar los datos del usuario" : undefined}>
                {rolesCatalogo.map(r => <option key={r.id} value={r.id}>{r.label}</option>)}
              </select>
            </div>
            <div style={{ display: "flex", flexDirection: "column" }}>
              <label style={labelStyle}>Teléfono</label>
              <input name="telefono" value={form.telefono} onChange={cambiar} style={inputStyle} placeholder="+34 600 000 000" disabled={!modDatos} title={!modDatos ? "No tienes permiso para modificar los datos del usuario" : undefined} />
            </div>
            <div style={{ display: "flex", flexDirection: "column" }}>
              <label style={labelStyle}>Teléfono Telnyx (llamadas CRM)</label>
              <input name="telefono_telnyx" value={form.telefono_telnyx} onChange={cambiar} style={inputStyle} placeholder="+34 900 000 000" disabled={!modDatos} title={!modDatos ? "No tienes permiso para modificar los datos del usuario" : undefined} />
            </div>
            <div style={{ display: "flex", flexDirection: "column" }}>
              <label style={labelStyle}>Agente asociado</label>
              <select name="agente_id" value={form.agente_id} onChange={cambiar} style={inputStyle} disabled={!modDatos} title={!modDatos ? "No tienes permiso para modificar los datos del usuario" : undefined}>
                <option value="">Sin asignar</option>
                {agentes.map(a => <option key={a.id} value={a.id}>{a.nombre}</option>)}
              </select>
            </div>
          </div>
          )}
          {!veDatos && vePassword && (
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(240px, 1fr))", gap: 16, marginBottom: 20 }}>
              <div style={{ display: "flex", flexDirection: "column" }}>
                <label style={labelStyle}>Contraseña de acceso al CRM{editando ? "" : " *"}</label>
                <PasswordInputWithToggle
                  inputRef={passwordInputRef}
                  name="password"
                  value={form.password}
                  onChange={cambiar}
                  placeholder={editando ? "Dejar vacío para no cambiar" : "Mín. 6 caracteres"}
                  inputStyle={inputStyle}
                  c={c}
                  visible={showPassword}
                  onToggleVisible={() => setShowPassword((v) => !v)}
                  disabled={!modPassword}
                  title={!modPassword ? "No tienes permiso para modificar la contraseña de acceso" : undefined}
                />
              </div>
            </div>
          )}

          {veSmtp && (
          <div style={{ marginBottom: 20, padding: "16px 0", borderTop: `1px solid ${c.border}` }}>
            <h4 style={{ fontSize: 14, fontWeight: 700, color: c.textPrimary, margin: "0 0 8px" }}>📧 Correo SMTP (envíos CRM)</h4>
            <div style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 8,
              padding: "7px 14px",
              borderRadius: 20,
              marginBottom: 12,
              background: form.smtp_configured ? "rgba(22,163,74,0.08)" : "rgba(245,158,11,0.08)",
              border: `1px solid ${form.smtp_configured ? "rgba(22,163,74,0.25)" : "rgba(245,158,11,0.25)"}`,
            }}>
              <span style={{
                width: 8, height: 8, borderRadius: "50%", flexShrink: 0,
                background: form.smtp_configured ? "#16a34a" : "#f59e0b",
              }} />
              <span style={{ fontSize: 13, fontWeight: 600, color: form.smtp_configured ? "#16a34a" : "#f59e0b" }}>
                {form.smtp_configured
                  ? "Contraseña del buzón guardada"
                  : "Sin contraseña del buzón — no puede enviar"}
              </span>
            </div>
            <p style={{ fontSize: 12, color: c.textMuted, margin: "0 0 14px", lineHeight: 1.45 }}>
              {form.smtp_configured
                ? "Verás •••••••••• si ya está puesta. Déjala así o escribe una nueva para cambiarla."
                : "Campo vacío = aún no hay contraseña. Escríbela y guarda para activar el envío."}
            </p>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(240px, 1fr))", gap: 16 }}>
              <div style={{ display: "flex", flexDirection: "column" }}>
                <label style={labelStyle}>Servidor SMTP</label>
                <input name="smtp_host" value={form.smtp_host} onChange={cambiar} style={inputStyle} disabled={!modSmtp} title={!modSmtp ? "No tienes permiso para modificar el correo SMTP" : undefined} />
              </div>
              <div style={{ display: "flex", flexDirection: "column" }}>
                <label style={labelStyle}>Puerto</label>
                <input name="smtp_port" type="number" value={form.smtp_port} onChange={cambiar} style={inputStyle} disabled={!modSmtp} title={!modSmtp ? "No tienes permiso para modificar el correo SMTP" : undefined} />
              </div>
              <div style={{ display: "flex", flexDirection: "column" }}>
                <label style={labelStyle}>Usuario / email buzón</label>
                <input name="smtp_user" value={form.smtp_user} onChange={cambiar} style={inputStyle} placeholder="tu@vitalcasa.com" disabled={!modSmtp} title={!modSmtp ? "No tienes permiso para modificar el correo SMTP" : undefined} />
              </div>
              <div style={{ display: "flex", flexDirection: "column" }}>
                <label style={labelStyle}>Contraseña del correo (buzón)</label>
                <PasswordInputWithToggle
                  inputRef={smtpPassInputRef}
                  name="smtp_pass"
                  value={form.smtp_pass}
                  onChange={(e) => {
                    setSmtpTestMsg("");
                    setSmtpTestOk(null);
                    cambiar(e);
                  }}
                  onFocus={() => {
                    setForm((prev) => (
                      prev.smtp_pass === SMTP_PASS_MASK ? { ...prev, smtp_pass: "" } : prev
                    ));
                  }}
                  onBlur={() => {
                    setForm((prev) => (
                      prev.smtp_configured && !String(prev.smtp_pass || "").trim()
                        ? { ...prev, smtp_pass: SMTP_PASS_MASK }
                        : prev
                    ));
                  }}
                  placeholder={form.smtp_configured ? "Escribe solo si quieres cambiarla" : "Contraseña del buzón de correo"}
                  inputStyle={inputStyle}
                  c={c}
                  visible={showSmtpPass}
                  onToggleVisible={() => setShowSmtpPass((v) => !v)}
                  disabled={!modSmtp}
                  title={!modSmtp ? "No tienes permiso para modificar el correo SMTP" : undefined}
                />
              </div>
            </div>
            {modSmtp && (
              <div style={{ marginTop: 14, display: "flex", flexWrap: "wrap", alignItems: "center", gap: 10 }}>
                <button
                  type="button"
                  onClick={probarCorreoSmtp}
                  disabled={probandoSmtp}
                  style={{
                    padding: "8px 16px",
                    borderRadius: 8,
                    border: `1px solid ${c.border}`,
                    background: isDark ? "rgba(255,255,255,0.06)" : "#fff",
                    color: c.textPrimary,
                    fontWeight: 600,
                    fontSize: 13,
                    cursor: probandoSmtp ? "wait" : "pointer",
                    opacity: probandoSmtp ? 0.7 : 1,
                  }}
                >
                  {probandoSmtp ? "Comprobando…" : "Probar correo (enviar + recibir)"}
                </button>
                <span style={{ fontSize: 12, color: c.textMuted }}>
                  Comprueba antes de guardar. No envía emails reales.
                </span>
              </div>
            )}
            {smtpTestMsg && (
              <div style={{
                marginTop: 12,
                padding: "10px 14px",
                borderRadius: 8,
                fontSize: 13,
                lineHeight: 1.45,
                background: smtpTestOk ? "rgba(22,163,74,0.08)" : "rgba(239,68,68,0.08)",
                border: `1px solid ${smtpTestOk ? "rgba(22,163,74,0.25)" : "rgba(239,68,68,0.25)"}`,
                color: smtpTestOk ? "#16a34a" : "#dc2626",
              }}>
                {smtpTestOk ? "✓ " : "✗ "}{smtpTestMsg}
              </div>
            )}
          </div>
          )}

          {/* Imágenes — solo disponibles si el usuario ya existe (tiene ID) */}
          {editando ? (
            <div style={{ padding: "20px 0", borderTop: `1px solid ${c.border}`, display: "flex", flexDirection: "column", gap: 24 }}>

              {/* Fila foto de perfil */}
              {veFoto && (
              <div style={{ display: "grid", gridTemplateColumns: "minmax(160px, 220px) auto 1fr", gap: 24, alignItems: "start" }}>
                {/* Zona de subida */}
                {modFoto ? (
                  <ImageUploadZone
                    label="📷 Foto de perfil"
                    hint="JPG o PNG · se usa como avatar en el CRM"
                    currentUrl={form.avatar_url}
                    fieldName="foto"
                    onUploaded={handleUploadFoto(editando)}
                    isDark={isDark}
                    colors={c}
                    aspectRatio="1 / 1"
                    objectFitMode="cover"
                  />
                ) : (
                  <div style={{ display: "flex", flexDirection: "column", gap: 6, opacity: 0.6 }} title="No tienes permiso para modificar la foto de perfil">
                    <label style={{ fontSize: 13, fontWeight: 600, color: c.textSecondary }}>📷 Foto de perfil</label>
                    {form.avatar_url ? (
                      <img src={form.avatar_url} alt="Foto de perfil" style={{ width: "100%", aspectRatio: "1 / 1", objectFit: "cover", borderRadius: 12, border: `2px solid ${c.border}`, objectPosition: form.avatar_position || "50% 50%" }} />
                    ) : (
                      <p style={{ fontSize: 12, color: c.textMuted }}>Sin foto</p>
                    )}
                  </div>
                )}
                {/* Divisor vertical */}
                {form.avatar_url && (
                  <div style={{ width: 1, alignSelf: "stretch", background: c.border, margin: "24px 0" }} />
                )}
                {/* Reposicionador — solo si hay foto y permiso de modificar */}
                {form.avatar_url && modFoto ? (
                  <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                    <label style={{ fontSize: 13, fontWeight: 600, color: c.textSecondary }}>
                      ✂️ Reposicionar encuadre
                    </label>
                    <p style={{ fontSize: 11, color: c.textMuted, margin: 0, lineHeight: 1.5 }}>
                      Así se verá el avatar en el CRM. Arrastra para mostrar la parte que prefieras y guarda la posición.
                    </p>
                    <AvatarReposicionador
                      src={form.avatar_url}
                      position={form.avatar_position}
                      onSave={(pos) => handleSavePosition(editando, pos)}
                      isDark={isDark}
                      colors={c}
                    />
                  </div>
                ) : modFoto ? (
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
                    <p style={{ fontSize: 12, color: c.textMuted, textAlign: "center" }}>
                      Sube una foto para poder reposicionar el encuadre
                    </p>
                  </div>
                ) : null}
              </div>
              )}

              {/* Fila tarjeta de visita */}
              {veTarjeta && (
              <div>
                {modTarjeta ? (
                  <ImageUploadZone
                    label="💼 Tarjeta de visita"
                    hint="JPG o PNG · la imagen completa sin recortar"
                    currentUrl={form.tarjeta_url}
                    fieldName="tarjeta"
                    onUploaded={handleUploadTarjeta(editando)}
                    isDark={isDark}
                    colors={c}
                    aspectRatio="856 / 540"
                    objectFitMode="contain"
                  />
                ) : (
                  <div style={{ opacity: 0.6 }} title="No tienes permiso para modificar la tarjeta de visita">
                    <label style={{ fontSize: 13, fontWeight: 600, color: c.textSecondary, display: "block", marginBottom: 6 }}>💼 Tarjeta de visita</label>
                    {form.tarjeta_url ? (
                      <img src={form.tarjeta_url} alt="Tarjeta de visita" style={{ width: "100%", maxWidth: 320, aspectRatio: "856 / 540", objectFit: "contain", borderRadius: 12, border: `2px solid ${c.border}` }} />
                    ) : (
                      <p style={{ fontSize: 12, color: c.textMuted }}>Sin tarjeta</p>
                    )}
                  </div>
                )}
              </div>
              )}

            </div>
          ) : (
            <p style={{ fontSize: 12, color: c.textMuted, padding: "8px 0", borderTop: `1px solid ${c.border}`, margin: "0 0 4px" }}>
              💡 Crea primero el usuario y luego podrás subir la foto de perfil y la tarjeta de visita desde Editar.
            </p>
          )}

          <div style={{ display: "flex", gap: 12, marginTop: 20 }}>
            <button onClick={guardar} style={{
              padding: "8px 20px", borderRadius: 8, border: "none", cursor: "pointer",
              background: "#16a34a", color: "#fff", fontWeight: 600, fontSize: 14,
            }}>{editando ? "Guardar cambios" : "Crear usuario"}</button>
            <button onClick={() => { setMostrarForm(false); setEditando(null); }} style={{
              padding: "8px 20px", borderRadius: 8, cursor: "pointer",
              background: "transparent", border: `1px solid ${c.border}`,
              color: c.textSecondary, fontWeight: 500, fontSize: 14,
            }}>Cancelar</button>
          </div>
        </div>
      )}

      {/* Tabla */}
      {cargando && !usuarios.length ? (
        <div style={{ textAlign: "center", padding: 60, color: c.textMuted }}>Cargando...</div>
      ) : usuarios.length === 0 ? (
        <div style={{ textAlign: "center", padding: 60, color: c.textMuted }}>
          <p style={{ fontWeight: 600, color: c.textSecondary }}>No hay usuarios</p>
          <p style={{ fontSize: 13, marginTop: 4 }}>Pulsa "+ Nuevo usuario" para crear el primero</p>
        </div>
      ) : (
        <div style={{ background: c.bgCard, borderRadius: 12, border: `1px solid ${c.border}`, overflow: "hidden" }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ borderBottom: `1px solid ${c.border}` }}>
                {["", "Nombre", "Email", "Rol", "Teléfono", "Telnyx", "Agente", "Correo", "Imágenes", "Estado", "Último acceso", "Acciones"].map(h => (
                  <th key={h} style={{
                    textAlign: h === "Acciones" ? "right" : (h === "Imágenes" || h === "Correo" ? "center" : "left"),
                    padding: "12px 14px",
                    fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em",
                    color: c.textMuted, background: isDark ? "rgba(255,255,255,0.03)" : "#f9fafb",
                  }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {usuarios.map((u, i) => {
                const rc = rolColor(u.rol);
                const iniciales = u.nombre ? inicialesNombreApellido(u.nombre) : "?";
                return (
                  <tr key={u.id} style={{
                    borderBottom: `1px solid ${c.border}`,
                    background: i % 2 === 0 ? "transparent" : (isDark ? "rgba(255,255,255,0.02)" : "#f9fafb"),
                    opacity: u.activo ? 1 : 0.5,
                  }}>
                    {/* Avatar */}
                    <td style={{ padding: "10px 14px", width: 48 }}>
                      {u.avatar_url ? (
                        <img src={u.avatar_url} alt={u.nombre}
                          style={{ width: 38, height: 38, borderRadius: "50%", objectFit: "cover", display: "block",
                            objectPosition: u.avatar_position || "50% 50%",
                            border: `2px solid ${c.border}` }} />
                      ) : (
                        <div style={{
                          width: 38, height: 38, borderRadius: "50%",
                          background: isDark ? "rgba(255,255,255,0.1)" : "#e2e8f0",
                          display: "flex", alignItems: "center", justifyContent: "center",
                          fontSize: 13, fontWeight: 700, color: c.textMuted,
                          border: `2px solid ${c.border}`,
                        }}>{iniciales}</div>
                      )}
                    </td>
                    <td style={{ padding: "10px 14px", fontSize: 14, fontWeight: 600, color: c.textPrimary }}>{u.nombre}</td>
                    <td style={{ padding: "10px 14px", fontSize: 13, color: c.textSecondary }}>{u.email}</td>
                    <td style={{ padding: "10px 14px" }}>
                      <span style={{
                        display: "inline-block", padding: "2px 10px", borderRadius: 20,
                        fontSize: 12, fontWeight: 600, background: rc.bg, color: rc.text, border: `1px solid ${rc.border}`,
                      }}>{rolLabel(u.rol)}</span>
                    </td>
                    <td style={{ padding: "10px 14px", fontSize: 13, color: c.textMuted }}>{u.telefono || "-"}</td>
                    <td style={{ padding: "10px 14px", fontSize: 13, color: c.textMuted, fontFamily: "monospace" }}>{u.telefono_telnyx || "-"}</td>
                    <td style={{ padding: "10px 14px", fontSize: 13, color: c.textSecondary }}>
                      {u.agente_id ? (agentes.find(a => a.id === u.agente_id)?.nombre || `#${u.agente_id}`) : "-"}
                    </td>
                    <td style={{ padding: "10px 14px", textAlign: "center" }}>
                      <span
                        title={u.smtp_configured ? "Contraseña del buzón guardada" : "Falta contraseña SMTP"}
                        style={{
                          display: "inline-block", padding: "2px 10px", borderRadius: 20,
                          fontSize: 11, fontWeight: 700,
                          background: u.smtp_configured ? "rgba(22,163,74,0.12)" : "rgba(245,158,11,0.12)",
                          color: u.smtp_configured ? "#16a34a" : "#d97706",
                          border: `1px solid ${u.smtp_configured ? "rgba(22,163,74,0.3)" : "rgba(245,158,11,0.3)"}`,
                        }}
                      >
                        {u.smtp_configured ? "OK" : "Falta"}
                      </span>
                    </td>
                    {/* Columna imágenes */}
                    <td style={{ padding: "10px 14px", textAlign: "center" }}>
                      <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 6 }}>
                        {u.avatar_url ? (
                          <a href={u.avatar_url} target="_blank" rel="noopener noreferrer" title="Ver foto de perfil"
                            style={{ display: "flex", alignItems: "center", justifyContent: "center",
                              width: 30, height: 30, borderRadius: 8,
                              background: isDark ? "rgba(255,255,255,0.07)" : "#f1f5f9",
                              border: `1px solid ${c.border}`, fontSize: 15, textDecoration: "none" }}>
                            📷
                          </a>
                        ) : (
                          <span title="Sin foto" style={{ fontSize: 15, opacity: 0.3, cursor: "default" }}>📷</span>
                        )}
                        {u.tarjeta_url ? (
                          <a href={u.tarjeta_url} target="_blank" rel="noopener noreferrer" title="Ver tarjeta de visita"
                            style={{ display: "flex", alignItems: "center", justifyContent: "center",
                              width: 30, height: 30, borderRadius: 8,
                              background: isDark ? "rgba(255,255,255,0.07)" : "#f1f5f9",
                              border: `1px solid ${c.border}`, fontSize: 15, textDecoration: "none" }}>
                            💼
                          </a>
                        ) : (
                          <span title="Sin tarjeta" style={{ fontSize: 15, opacity: 0.3, cursor: "default" }}>💼</span>
                        )}
                      </div>
                    </td>
                    <td style={{ padding: "10px 14px" }}>
                      {veActivar && (
                        <button onClick={() => modActivar && toggleActivo(u)} disabled={!modActivar}
                          title={!modActivar ? "No tienes permiso para activar/desactivar usuarios" : (u.activo ? "Desactivar" : "Activar")} style={{
                          padding: "2px 10px", borderRadius: 20, fontSize: 12, fontWeight: 600, cursor: modActivar ? "pointer" : "not-allowed",
                          border: "none", opacity: modActivar ? 1 : 0.6,
                          background: u.activo ? "#d1fae5" : "#fee2e2",
                          color: u.activo ? "#065f46" : "#991b1b",
                        }}>{u.activo ? "Activo" : "Inactivo"}</button>
                      )}
                    </td>
                    <td style={{ padding: "10px 14px", fontSize: 12, color: c.textMuted }}>
                      {u.ultimo_acceso
                        ? new Date(u.ultimo_acceso).toLocaleDateString("es-ES", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })
                        : "Nunca"}
                    </td>
                    <td style={{ padding: "10px 14px", textAlign: "right" }}>
                      <button onClick={() => abrirEditar(u)} style={{
                        background: "none", border: "none", cursor: "pointer", fontSize: 13, fontWeight: 600,
                        color: c.accentBlue, marginRight: 12,
                      }}>Editar</button>
                      {puedeEliminarUsuario && (
                        <button onClick={() => eliminar(u)} style={{
                          background: "none", border: "none", cursor: "pointer", fontSize: 13, fontWeight: 600, color: "#ef4444",
                        }}>Eliminar</button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
